<template>
  <div>
    <div class="section-header">
      <h4 v-if="canTagReviews" class="section-header__title">
        {{ $t("message.reviews.tags.reviewTags") }}
        <ReviewsCategoriesProcessingTooltip
          v-if="categorizationInProgress"
          :label="$t('message.sentiment.reviewsProcessingNumber3')"
        />
      </h4>

      <div
        v-if="showMessage"
        class="message"
        :class="{ 'no-selected': !noTagsAvailable }"
      >
        {{ messageText }}
      </div>
    </div>
    <v-skeleton-loader
      v-if="showLoader"
      class="loader"
      type="chip@4"
      indeterminate
      color="primary"
    ></v-skeleton-loader>
    <template v-else>
      <div class="tags-list-container" :class="{ 'mt-4': !canTagReviews }">
        <div class="tags-list">
          <template v-for="tag in visibleTags">
            <TagsManagerListItem
              v-model="tagsState[tag.id]"
              :key="tag.id"
              :tag="tag"
              :max-characters="15"
              :toggle-enabled="canTagReviews"
              :is-processing="categorizationInProgress || readOnly"
              @change="onTagToggle(tag.id, $event)"
              @change-sentiment="onSentimentChange(tag.id, $event.sentiment)"
            />
          </template>
        </div>
        <button
          v-if="showExpandButton"
          class="tags-list__more"
          @click="toggleTagsPreviewDialog"
        >
          {{ $t("message.common.showMore") }}
        </button>
      </div>
    </template>

    <SentimentChangeAlert
      :show="showAlert"
      @close="closeAlertDialog"
      @confirm="toggleTagHandler(true)"
    />
    <TagsPreviewDialog
      v-if="showTagsPreviewDialog"
      :is-open="showTagsPreviewDialog"
      :review="review"
      :tags="availableTags"
      @close="onTagsPreviewClose"
    />
  </div>
</template>

<script>
import TagsManagerListItem from "@/components/Tags/TagsManagerListItem";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import ReviewsCategoriesProcessingTooltip from "@/components/Reviews/Categories/ReviewsCategoriesProcessingTooltip.vue";
import SentimentChangeAlert from "@/components/Reviews/SentimentChangeAlert.vue";
import reviewTagsMixin from "@/mixins/review-tags";

export default {
  name: "ReviewTagsSelector",
  components: {
    TagsManagerListItem,
    ReviewsCategoriesProcessingTooltip,
    TagsPreviewDialog: () =>
      import("@/components/Reviews/TagsPreviewDialog.vue"),
    SentimentChangeAlert
  },
  mixins: [permissionsInfo, reviewTagsMixin],
  props: {
    isLoading: {
      type: Boolean,
      default: true
    },
    availableTags: {
      type: Array,
      default: () => []
    },
    review: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    selectedOnly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showTagsPreviewDialog: false
    };
  },
  computed: {
    showMessage() {
      return this.noTagsSelected || this.noTagsAssigned || this.noTagsAvailable;
    },
    messageText() {
      if (this.noTagsSelected) {
        return this.$t("message.reviews.tags.noTagsSelected");
      }
      if (this.noTagsAssigned) {
        return this.$t("message.reviews.tags.noTagsAssigned");
      }
      return this.$t("message.reviews.tags.noTagsAvailable");
    },
    showExpandButton() {
      return this.sortedTags.length > 5;
    },
    showLoader() {
      return this.isLoading;
    },
    sortedTags() {
      return [...this.availableTags].sort((a, b) => {
        const aActive = this.tagsState[a.id].isActive;
        const bActive = this.tagsState[b.id].isActive;
        return bActive - aActive;
      });
    },
    activeTagsCount() {
      return Object.values(this.tagsState).filter(({ isActive }) => isActive)
        .length;
    },
    selectedTags() {
      return Object.values(this.tagsState).filter(({ isActive }) => isActive);
    },
    visibleTags() {
      if (this.activeTagsCount <= 5) {
        return this.sortedTags.slice(0, 5);
      } else if (this.activeTagsCount > 5) {
        return this.activeTags;
      }
      return this.selectedTags;
    }
  },

  methods: {
    onTagsPreviewClose() {
      this.showTagsPreviewDialog = false;
      this.$emit("tags-preview-close");
    },
    toggleTagsPreviewDialog() {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORIES_SHOW_MORE,
        review_id: this.review.id
      });
      this.showTagsPreviewDialog = !this.showTagsPreviewDialog;
    }
  }
};
</script>

<style lang="scss" scoped>
.loader {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;
  padding-left: $spacing * 2;

  ::v-deep .v-skeleton-loader__chip {
    height: 46px;
    border-radius: 44px;
    background: rgba(0, 0, 0, 0.09);
  }
}

.tags-list-container {
  margin: $spacing * -1.25;
  display: flex;
  flex-direction: column;
  padding-bottom: $spacing;
}

.tags-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: $spacing * 1.25;

  &__more {
    @include font-size(14px);
    line-height: 16px;
    color: $primary;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.section-header {
  position: relative;
  margin-left: $spacing * 2;

  &__title {
    display: flex;
    align-items: center;
    gap: $spacing;
  }
}

h4 {
  @include sectionTitle();
  font-weight: 500;
  margin: $spacing * 3 0;
}

.message {
  @include font-size(11px);
  line-height: 14px;
  font-weight: 400;
  white-space: pre-line;
}

.no-selected {
  position: absolute;
  bottom: $spacing * -2;
}

.link-btn {
  font-weight: 500;
  color: $primary;
  cursor: pointer;
  user-select: none;

  &:hover {
    text-decoration: underline;
  }
}

.tags-readonly {
  @include font-size(12px);
  line-height: 16px;
  font-weight: normal;
  color: $dark-blue-3;
  margin-left: $spacing * 2;
}
.alert-text {
  color: $dark-blue-3;
  @include font-size(16px);
}
</style>
